import React from "react"
import {
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ListItem,
  UnorderedList,
  VStack,
  Container,
  Image,
} from "@chakra-ui/react"
import figure1 from "../../../images/allstar/key-findings-figure-1-2025.png"
import figure2 from "../../../images/allstar/key-findings-figure-2-2025.png"
import figure3 from "../../../images/allstar/key-findings-figure-3-2025.png"
import figure4a from "../../../images/allstar/key-findings-figure-4a-2025.png"
import figure4b from "../../../images/allstar/key-findings-figure-4b-2025.png"

const KeyFindingsContent25 = () => {
  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={6} align="stretch">
        <Text>
          Avant-garde Health's 2025 Research All-Stars identifies and highlights
          America's most influential physician-researchers and leading
          hospitals, whose groundbreaking work significantly impacts clinical
          practice and patient outcomes nationwide. By analyzing trends across
          specialties, geography, and career stages, this report provides unique
          insights into where and how healthcare innovation thrives. These
          findings offer strategic benchmarks for hospitals and clinical leaders
          to strengthen their research footprint, attract exceptional talent,
          and position themselves at the forefront of healthcare excellence.
        </Text>

        <Heading as="h3" size="md">
          The Scope of Research Excellence
        </Heading>
        <Text>
          This year's Research All-Stars analysis evaluated and identified 489
          leading hospitals and more than 21,000 physician researchers across 22
          medical and surgical specialties. These numbers highlight the
          extraordinary breadth of research occurring nationwide, from large
          academic medical centers to specialized clinical hubs.
        </Text>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Overall</Th>
              <Th isNumeric>N Hospital All-Stars</Th>
              <Th isNumeric>N Physician All-Stars</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Overall</Td>
              <Td isNumeric>92</Td>
              <Td isNumeric>NA</Td>
            </Tr>
          </Tbody>
        </Table>

        <Text>
          <strong>Surgical Specialties</strong>
        </Text>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Overall</Th>
              <Th isNumeric>N Hospital All-Stars</Th>
              <Th isNumeric>N Physician All-Stars</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Orthopedic Surgery</Td>
              <Td isNumeric>147</Td>
              <Td isNumeric>1,702</Td>
            </Tr>
            <Tr>
              <Td>Sports Medicine</Td>
              <Td isNumeric>12</Td>
              <Td isNumeric>114</Td>
            </Tr>
            <Tr>
              <Td>Hand Surgery</Td>
              <Td isNumeric>8</Td>
              <Td isNumeric>103</Td>
            </Tr>
            <Tr>
              <Td>Neurosurgery & Spine</Td>
              <Td isNumeric>63</Td>
              <Td isNumeric>448</Td>
            </Tr>
            <Tr>
              <Td>Cardiothoracic Surgery</Td>
              <Td isNumeric>45</Td>
              <Td isNumeric>313</Td>
            </Tr>
            <Tr>
              <Td>Vascular Surgery</Td>
              <Td isNumeric>42</Td>
              <Td isNumeric>269</Td>
            </Tr>
            <Tr>
              <Td>ENT (Otolaryngology)</Td>
              <Td isNumeric>91</Td>
              <Td isNumeric>505</Td>
            </Tr>
            <Tr>
              <Td>GI/General Surgery</Td>
              <Td isNumeric>159</Td>
              <Td isNumeric>1,239</Td>
            </Tr>
            <Tr>
              <Td>Urology</Td>
              <Td isNumeric>88</Td>
              <Td isNumeric>500</Td>
            </Tr>
            <Tr>
              <Td>Surgical Oncology</Td>
              <Td isNumeric>18</Td>
              <Td isNumeric>82</Td>
            </Tr>
            <Tr>
              <Td>Ophthalmology</Td>
              <Td isNumeric>99</Td>
              <Td isNumeric>1,289</Td>
            </Tr>
            <Tr>
              <Td>Gynecologic Surgery</Td>
              <Td isNumeric>143</Td>
              <Td isNumeric>2,748</Td>
            </Tr>
          </Tbody>
        </Table>

        <Text>
          <strong>Medical Specialties</strong>
        </Text>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Overall</Th>
              <Th isNumeric>N Hospital All-Stars</Th>
              <Th isNumeric>N Physician All-Stars</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Internal Medicine</Td>
              <Td isNumeric>182</Td>
              <Td isNumeric>4,857</Td>
            </Tr>
            <Tr>
              <Td>Cardiology</Td>
              <Td isNumeric>127</Td>
              <Td isNumeric>1,558</Td>
            </Tr>
            <Tr>
              <Td>Pulmonology</Td>
              <Td isNumeric>92</Td>
              <Td isNumeric>731</Td>
            </Tr>
            <Tr>
              <Td>Gastroenterology</Td>
              <Td isNumeric>99</Td>
              <Td isNumeric>987</Td>
            </Tr>
            <Tr>
              <Td>Endocrinology</Td>
              <Td isNumeric>52</Td>
              <Td isNumeric>464</Td>
            </Tr>
            <Tr>
              <Td>Nephrology</Td>
              <Td isNumeric>79</Td>
              <Td isNumeric>623</Td>
            </Tr>
            <Tr>
              <Td>Neurology</Td>
              <Td isNumeric>94</Td>
              <Td isNumeric>1,337</Td>
            </Tr>
            <Tr>
              <Td>Hematology</Td>
              <Td isNumeric>83</Td>
              <Td isNumeric>796</Td>
            </Tr>
            <Tr>
              <Td>Medical Oncology</Td>
              <Td isNumeric>29</Td>
              <Td isNumeric>185</Td>
            </Tr>
            <Tr>
              <Td>Rheumatology</Td>
              <Td isNumeric>49</Td>
              <Td isNumeric>364</Td>
            </Tr>
          </Tbody>
        </Table>

        <Text>
          Our analysis highlights significant geographic clusters of research
          excellence, notably concentrated within major academic medical centers
          along coastal regions, around the Great Lakes, and within key urban
          hubs. As illustrated in Figure 1, these geographic hotspots reflect
          not only the presence of institutional resources but also strategic
          opportunities for hospitals to recruit and retain top-tier research
          talent. Figure 2 further emphasizes this trend, identifying
          California, New York, Massachusetts, and Texas as states leading in
          physician research activity—regions where competition for skilled
          researchers and clinicians is particularly intense.
        </Text>

        <Text>
          <strong>Figure 1.</strong>
        </Text>

        <Image src={figure1} alt="Figure 1" />

        <Text>
          <strong>Figure 2.</strong>
        </Text>

        <Image src={figure2} alt="Figure 2" />

        <Heading as="h3" size="md">
          Generational Shifts: Research Leadership Across Career Stages
        </Heading>
        <Text>
          Figure 3 highlights the broad generational distribution of
          physician-researchers, emphasizing the sustained influence of
          researchers who graduated in the 1990s and 2000s, while notably
          revealing a significant rise in younger leaders emerging from more
          recent graduating classes. This generational diversity signals a
          healthy pipeline for future academic leadership, ensuring continuity
          in clinical innovation and institutional reputation. Hospitals and
          academic centers may leverage these insights to proactively develop
          mentorship, leadership succession, and recruitment strategies to
          maintain their competitive edge.
        </Text>

        <Text>
          <strong>Figure 3.</strong>
        </Text>

        <Image src={figure3} alt="Figure 3" />

        <Heading as="h3" size="md">
          The Relationship Between Research and Clinical Volume
        </Heading>
        <Text>
          One of the most significant insights from our 2025 analysis is the
          striking difference in how research productivity interacts with
          clinical workload across medical and surgical specialties.
          Specifically, in medical specialties, there is a pronounced inverse
          relationship: physicians with lower clinical workloads (measured by
          Relative Value Units, or RVUs) exhibit notably higher research
          productivity. Those in the lowest RVU quintile achieved an average
          publication score of 8.6, while the highest quintile averaged only
          3.2—reflecting the impact of protected research time, grant-funded
          roles, and dedicated academic positions.
        </Text>
        <Text>
          Conversely, surgical specialties show a fundamentally different
          pattern, with publication scores consistently stable (around 2.7 to
          3.0) across varying clinical workloads. This finding underscores that
          surgical research activities—such as procedural innovations, outcome
          tracking, and participation in clinical trials—are effectively
          integrated into daily practice.
        </Text>
        <Text>
          These divergent trends carry crucial implications for institutional
          leadership and workforce strategy. Medical specialty departments may
          require deliberate strategies, including protected academic time and
          dedicated research support, to enhance scholarly productivity.
          Surgical departments, by contrast, might further leverage their
          existing integration of research into clinical workflows as a
          strategic advantage in recruitment, reputation-building, and practice
          innovation.
        </Text>

        <Text>
          <strong>Figure 4.</strong>
        </Text>

        <Image src={figure4a} alt="Figure 4a" />

        <Image src={figure4b} alt="Figure 4b" />

        <Heading as="h3" size="md">
          Why This Matters
        </Heading>
        <Text>
          At Avant-garde Health, we recognize that cultivating research
          excellence directly enhances institutional reputation, attracts
          top-tier talent, and most importantly, leads to better patient care
          and outcomes. Understanding these insights equips hospitals,
          healthcare executives, and clinical leaders to strategically nurture
          research productivity and effectively integrate scholarship into
          clinical practice.
        </Text>

        <Heading as="h4" size="md">
          For Hospital Executives and Department Leaders:
        </Heading>
        <UnorderedList spacing={2}>
          <ListItem>
            <strong>Benchmark Your Institution:</strong> Clearly understand
            where your hospital stands in research productivity compared to peer
            institutions, enabling targeted improvements.
          </ListItem>
          <ListItem>
            <strong>Strategic Resource Allocation:</strong> Recognize the
            distinct dynamics of research and clinical productivity in medical
            versus surgical specialties, informing smarter decisions about
            workload balance and investment.
          </ListItem>
          <ListItem>
            <strong>Talent Recruitment and Retention:</strong> Leverage these
            insights to create competitive recruitment and retention
            strategies—including protected research time, grant support, and
            leadership training programs—to attract and keep outstanding
            physician-researchers.
          </ListItem>
        </UnorderedList>

        <Heading as="h4" size="md">
          For Physicians and Clinical Researchers:
        </Heading>
        <UnorderedList spacing={2}>
          <ListItem>
            <strong>Personal Benchmarking:</strong> See how your scholarly
            contributions rank within your specialty and on a national level,
            offering clear visibility into your academic impact.
          </ListItem>
          <ListItem>
            <strong>Advocacy for Support Structures:</strong> Understand and
            advocate effectively for institutional resources, protected research
            time, and career development pathways that facilitate high-quality
            research alongside clinical practice.
          </ListItem>
          <ListItem>
            <strong>Recognition and Visibility:</strong> Gain recognition as a
            member of a distinguished national cohort of physician-researchers,
            enhancing both your personal profile and your institution’s
            reputation in healthcare innovation and academic excellence.
          </ListItem>
        </UnorderedList>

        <Text>
          By embracing these insights, healthcare leaders and practitioners
          alike can actively shape an environment that prioritizes and sustains
          meaningful research—driving continuous improvement in patient care and
          clinical innovation nationwide.
        </Text>
      </VStack>
    </Container>
  )
}

export default KeyFindingsContent25
